import { HOST_RPC_DOMAINS } from "../hostRpcDomains";
import { PATCHABLE_TARGETS } from "../patchable";
import { SDK_READ, SDK_WRITE } from "../readOnlyRpcs";
import { tool, type ToolDescriptor } from "../tool";

/**
 * The generic host RPC surface: describe, read, write and patch — the design in
 * `docs/tool-surface.md` § 4–§ 5.
 *
 * ## Why this exists at all, when `docs/tool-surface.md` said it must not
 *
 * The main spec's `docs/tool-surface.md` says "there is no untyped passthrough in production", and
 * its reasoning was sound: a host call is positional and untyped through
 * `rpc.invoke`, some host functions FAIL SOFT, and `InvocationError` seals
 * `procedureName`, `cause` and `previousRejectReasons` — so a bad guess yields
 * a plausible-looking empty result with the explaining exception sealed.
 *
 * **That reasoning assumed the guess could not be removed.** It can:
 * `hostRpcSignatures.ts` carries the declared parameter list, so
 * `giraffe_sdk_describe` replaces guessing with reading, and
 * `assertHostRpcName` turns a phantom name from silence into a named error.
 * The two properties that made a passthrough indefensible are the two these
 * tools supply.
 *
 * **No declared tool wraps a single host RPC (#38).** `giraffe_flyto` and
 * `giraffe_viewport` did, and duplicated this pair under second names. What such
 * a wrapper knew that the declaration does not is served by
 * `giraffe_sdk_describe` from `manifest/hostRpcNotes.ts`. A declared host
 * tool has to earn its place by doing something one SDK call cannot.
 *
 * ## The control frame guarantees these; any app MAY declare them
 *
 * The control frame always declares `giraffe_sdk_read` and `giraffe_sdk_write`. Another app
 * may declare them too, which is its author's choice and not ours (#25, amended 2026-09-22).
 * It is routed by declared capability like every other frame-answered tool, and
 * the newest declaring frame answers — see `relay/sessions.ts`'s
 * `resolveImplementor`.
 *
 * ## What the one-panel limit costs (#25)
 *
 * ⚠️ **Giraffe mounts ONE app panel at a time**, so unless the open app has
 * declared these itself, an agent can hold them or the open app's tools, never both. MEASURED on `/v4/` and `/v6/`
 * (`docs/error-taxonomy.md` § 3) and then **observed live in
 * both directions** the same day. This is not a theoretical limit; it is what
 * a demo does.
 *
 * The alternative considered was **containment by origin**: let any first-party
 * app answer these, and have the relay refuse the route unless the frame's
 * `appOrigin` is on an allowlist it holds itself. It is buildable today —
 * `appOrigin` is browser-attested rather than self-reported, see
 * `wire/protocol.ts` — and it was **rejected on cost to app authors**, not on
 * feasibility: it makes every first-party app bundle this handler, and that
 * bundle is redundant the day the MCP surface is served from an API rather than
 * bridged through an iframe. Pushing a throwaway requirement onto app authors
 * to fix an interim is the wrong trade. Leaving it to each author to opt in
 * removes the requirement.
 *
 * ⚠️ **Nothing restricts who may answer.** `resolveImplementor` selects any frame
 * that declares the name, so a third-party app that declared these would be
 * routed them and could answer the agent with whatever it liked. The boundary
 * that would restrict it is #22's `Origin` check, and that is a separate decision.
 *
 * What is live in the meantime is honesty: `relay/core.ts`'s
 * `PANEL_SWAP_COST` makes `app_not_open` say what opening the control panel
 * costs, and names the two-panel layout, which is the only arrangement that
 * holds both at once.
 */

export const giraffeSdkDescribe: ToolDescriptor = tool({
  name: "giraffe_sdk_describe",
  description:
    "Read the Giraffe host's RPC surface. With a query — the words for what you want to do, " +
    'such as "hide a layer" — it returns the best-matching procedures with their full ' +
    "signatures and the best-matching recipes (ordered multi-call workflows). Each result " +
    "carries the notes attached to it: traps you would otherwise learn by failing, and how to " +
    "tell look-alike procedures apart, delivered whether or not your words named them. Read " +
    "them before calling. With a name — a procedure, state key, recipe or note id from a " +
    "result — it returns that in full. With no arguments it returns the domains below with " +
    "the names to open first, " +
    "and every registered procedure name. Call this before " +
    "giraffe_sdk_read or giraffe_sdk_write rather than guessing an argument list: the arguments are positional " +
    "and a wrong guess can return a plausible empty result rather than an error. " +
    "What the host covers, one line per area:" +
    HOST_RPC_DOMAINS.map((d) => `\n- ${d.id}: ${d.line}`).join(""),
  inputSchema: {
    type: "object",
    properties: {
      name: {
        type: "string",
        description:
          "A procedure, state key, recipe or note id from a result, to describe in full. Omit " +
          "to list every registered procedure name instead.",
      },
      query: {
        type: "string",
        description:
          "What you want to do, in plain words. Returns the best-matching procedures and " +
          "recipes with the notes attached to each. Not together with name.",
      },
    },
    additionalProperties: false,
  },
  hostRpcNames: [],
  settlesBeforeResolving: true,
  canAwaitUser: false,
});

const SDK_CALL_SCHEMA: ToolDescriptor["inputSchema"] = {
  type: "object",
  properties: {
    fn: {
      type: "string",
      description: "The exact procedure name, as returned by giraffe_sdk_describe.",
    },
    args: {
      type: "array",
      description:
        "Positional arguments, in the order the signature declares them. Omit for a " +
        "procedure that takes none.",
    },
  },
  required: ["fn"],
  additionalProperties: false,
};

/**
 * The SDK passthrough, split by what a procedure does to the project (`manifest/readOnlyRpcs.ts`),
 * so the user's MCP client can grant reads and writes separately.
 *
 * `settlesBeforeResolving` is FALSE for both: `flyTo` is a read here, and it starts an animation
 * and returns.
 */
export const giraffeSdkRead: ToolDescriptor = tool({
  name: SDK_READ,
  description:
    "Call one Giraffe host RPC procedure that saves nothing to the project: reads, and changes " +
    "to what the user sees, such as the camera, selection, highlights, temporary layers and " +
    "popups. Call giraffe_sdk_describe first: it gives the signature, and its tool field says " +
    "whether giraffe_sdk_read or giraffe_sdk_write runs the procedure. Arguments are positional, " +
    "not named. A procedure that changes the project is refused here and nothing is sent; call " +
    "giraffe_sdk_write for it. A resolved result means the host accepted the call, not that its " +
    "effects have settled. Some procedures fail soft and return an empty value rather than " +
    "throwing, so check the result against what the signature says it returns.",
  inputSchema: SDK_CALL_SCHEMA,
  hostRpcNames: [],
  settlesBeforeResolving: false,
  canAwaitUser: false,
});

/**
 * ⚠️ **It carries no idempotency key, and `docs/mcp-conformance.md` requires one of a mutating
 * tool** — a conformant MCP client is REQUIRED to re-issue a request whose response stream broke.
 * A generic passthrough cannot invent a key per procedure. This is an ACCEPTED RISK, decided
 * 2026-09-14, not an oversight: the description says so, `destructiveHint` is true, and under P6
 * the client's permission prompt is the gate. See `docs/tool-surface.md` § 4.
 */
export const giraffeSdkWrite: ToolDescriptor = tool({
  name: SDK_WRITE,
  description:
    "Call one Giraffe host RPC procedure that changes the user's project. Call " +
    "giraffe_sdk_describe first: it gives the signature, and its tool field says whether " +
    "giraffe_sdk_read or giraffe_sdk_write runs the procedure. Arguments are positional, not " +
    "named. NOT IDEMPOTENT: this call must not be re-issued if its response is lost, because a " +
    "repeat would apply the write twice. A procedure that saves nothing is refused here and " +
    "nothing is sent; call giraffe_sdk_read for it. A resolved result means the host accepted " +
    "the call, not that its effects have settled. Some procedures fail soft and return an empty " +
    "value rather than throwing, so check the result against what the signature says it returns.",
  inputSchema: SDK_CALL_SCHEMA,
  hostRpcNames: [],
  settlesBeforeResolving: false,
  canAwaitUser: false,
  destructiveHint: true,
});

/**
 * A safety primitive, not a wrapper (#41).
 *
 * #38 says no declared tool wraps a single host RPC. This wraps none: it is
 * generic over any record the host can only write whole (`manifest/patchable.ts`),
 * and knows nothing about measures or URLs — those are recipes. What it adds is
 * something one SDK call cannot do: read, modify and write in one step in the
 * frame, with preconditions, then confirm from the host's own push.
 *
 * It exists because the obvious use of the host's write is destructive and
 * silent: a partial `public` resets a project's analytics, a partial
 * `featureCategories` wipes every usage, and neither errors. See
 * `manifest/patchable.ts` for the host behaviour.
 *
 * `settlesBeforeResolving` is TRUE, unlike the SDK tools: the frame resolves only
 * after the host's push shows the change.
 */
const giraffeSdkPatch: ToolDescriptor = tool({
  name: "giraffe_sdk_patch",
  description:
    "Change part of a Giraffe record that the host can only write whole, without sending the " +
    "whole record. Pass a JSON Patch (RFC 6902: test, replace, add, remove). The app reads the " +
    "record fresh, applies the operations to a copy, writes it back, and waits until the host " +
    "shows the change. Put a test before each change, on the id of the item you mean: if a test " +
    "fails nothing is written, and conflict says what is there instead. written: false with " +
    "conflict: null means the record already held these values, which is what a repeated call " +
    "returns, so a patch of test and replace or test and remove is safe to re-send; an add is " +
    'not, because re-sending it adds twice. confirmed: "host_state" means the change is in the ' +
    "project on screen, not that Giraffe's server has saved it. undo is a patch that puts it " +
    "back; it carries no test, so apply it only after re-reading the record. The host drops " +
    "fields set to null, so a null reads back as absent. Use this, never giraffe_sdk_write, to " +
    "change an app record.",
  inputSchema: {
    type: "object",
    properties: {
      target: {
        type: "string",
        enum: PATCHABLE_TARGETS,
        description:
          "The kind of record. projectApp is one app's record, as giraffe_state's " +
          "projectAppsByAppID shows it.",
      },
      id: {
        type: "string",
        description:
          'Which record. For projectApp, the app id; only "1", the project\'s analytics, is ' +
          "accepted today.",
      },
      ops: {
        type: "array",
        description:
          "JSON Patch operations, applied in order, all or nothing. Paths are JSON Pointers " +
          "into the record, e.g. /public/counter/measures/3/name.",
      },
    },
    required: ["target", "id", "ops"],
    additionalProperties: false,
  },
  hostRpcNames: [],
  settlesBeforeResolving: true,
  canAwaitUser: false,
  destructiveHint: true,
});

/**
 * What the FRAME declares for `giraffe_sdk_patch`: the relay's plan
 * (`manifest/patchable.ts`'s `PatchPlan`), not the agent's arguments.
 *
 * Same name, different schema, on purpose. `bridge/dispatcher.ts` validates
 * what arrives against what the frame declares, with `additionalProperties:
 * false`, and what arrives is the plan. The relay's catalogue and argument
 * check come from `SDK_TOOLS` (`relay/core.ts`'s `BY_NAME`), so the agent never
 * sees this schema. It is never in `SDK_TOOLS`.
 */
export const giraffeSdkPatchPlan: ToolDescriptor = tool({
  name: "giraffe_sdk_patch",
  description: giraffeSdkPatch.description,
  inputSchema: {
    type: "object",
    properties: {
      stateKey: { type: "string" },
      id: { type: "string" },
      writeFn: { type: "string" },
      ops: { type: "array" },
    },
    required: ["stateKey", "id", "writeFn", "ops"],
    additionalProperties: false,
  },
  hostRpcNames: [],
  settlesBeforeResolving: true,
  canAwaitUser: false,
  destructiveHint: true,
});

/**
 * ⚠️ TRANSITIONAL — `giraffe_sdk_describe` left this array when it became
 * relay-answered, and the array itself is deleted in the task that moves
 * the SDK tools into `HOST_TOOLS`. Nothing new should join it — except
 * `giraffe_sdk_patch` (#41), which needs the same origin-allowlisted routing
 * (`relay/sessions.ts`'s `SDK_TOOL_NAMES`) and moves with them.
 */
export const SDK_TOOLS: readonly ToolDescriptor[] = [
  giraffeSdkRead,
  giraffeSdkWrite,
  giraffeSdkPatch,
];
